import { getDefinition, hasDefinition, getTypedInheritanceChain } from '../classes/object-definition';
import { propertyConverters } from '../converters/converter';
import { serdeGetType } from './compound-serde';
// import { getOriginalNamedOutput } from '../decorators/json-property';
export function deserialize(object, propertyDef, type, options = { runConstructor: false, validate: true }, genericType) {
    if (object && object.constructor === Array) {
        return object.map((o) => deserializeRootObject(o, propertyDef, type, options, genericType));
    }
    return deserializeRootObject(object, propertyDef, type, options, genericType);
}
function deserializeRootObject(object, property, objectType = Object, options, genericType) {
    if (!hasDefinition(objectType)) {
        return object;
    }
    const values = object;
    const types = getTypedInheritanceChain(objectType, values);
    const type = types[0];
    const output = Object.create(type.prototype);
    const definitions = types.map((t) => getDefinition(t)).reverse(); // getTypeInheritanceChain returns object definitions only
    const additionals = definitions.filter((d) => d.additionalProperties);
    const keys = additionals.length === 0 ? undefined : new Set();
    for (const d of definitions) {
        // Ajv's JSON schemas include links to schemas for properties of classes. As a consequence any
        // property of a JsonSchema class will be validated against the schema the property was defined as
        // when the containing class is validated. What we have left to do is validate any sub or super classes
        // of that property. For performance we can skip re-validating the type that was defined in the
        // containing class (as the containing class already did it).
        if (options.validate && d !== property && d.validate)
            d.validate(values);
        if (options.runConstructor) {
            d.ctr.call(output);
        }
        d.beforeDeserialized.call(output);
        for (const [key, p] of d.properties) {
            if (!p.type) {
                throw new Error(`Cannot deserialize property '${key.toString()}' without type!`);
            }
            // Used when the field is Generic Type. Set the field's desired type
            if (genericType && genericType.get(key)) {
                p.type = genericType.get(key);
            }
            const value = values[p.serializedName];
            if (value === undefined || p.readonly) {
                continue;
            }
            if (keys)
                keys.add(p.serializedName);
            // key is the serialized name, so use original name when setting the output class instance object
            const objPropName = p.hasJsonProperty ? p.originalName : key;
            if (p.array || p.set) {
                output[objPropName] = deserializeArray(value, p, options, genericType);
                if (p.set) {
                    output[objPropName] = new Set(output[key]);
                }
                continue;
            }
            // set output to "original" name for jsonProperty since we want to deserialize into the proper class
            // we've also already validated, so no need to worry here
            output[objPropName] = deserializeObject(value, p, options, genericType);
        }
    }
    if (keys) {
        if (additionals.length > 1)
            throw new Error('Invalid schema: additionalProperties cannot be defined on both a child and its parent classes.');
        const additional = additionals[0].additionalProperties;
        for (const key in values) {
            if (keys.has(key))
                continue; // skip properties that were explicitly declared in schema
            const value = values[key];
            if (value === undefined || additional.readonly) {
                continue;
            }
            if (additional.array || additional.set) {
                const arr = deserializeArray(value, additional, options, genericType);
                if (additional.set) {
                    output[key] = new Set(arr);
                }
                else {
                    output[key] = arr;
                }
                continue;
            }
            output[key] = deserializeObject(value, additional, options, genericType);
        }
    }
    definitions.forEach((d) => {
        d.onDeserialized.call(output);
    });
    return output;
}
function deserializeArray(array, definition, options, genericType) {
    return array.map((v) => deserializeObject(v, definition, options, genericType));
}
function deserializeObject(object, definition, options, genericType) {
    const { typeToUse, isPrimitive } = serdeGetType(object, definition);
    const value = object;
    const converter = definition.converter || propertyConverters.get(definition.type);
    try {
        if (converter) {
            return converter.deserialize(value);
        }
    }
    catch (e) {
        // if we have something like
        //
        //	@JsonConverter(new DateConverter('date-time'))
        //	readonly value: Date | DateToolVariable;
        //
        // and the value is a dateToolVariable, we don't want to throw, and want to ignore this converter and continue
        // serdeGetType would have already picked out the proper type, DateToolVariable, already
        if (!definition.compoundType)
            throw e;
        console.info('Failed deserializing compound type with a converter, trying other non-primitive type');
    }
    if (!isPrimitive) {
        const objDefinition = getDefinition(typeToUse);
        if (objDefinition) {
            // Ajv's schemas include all schemas of nested objects. Validating the property object also validates
            // all nested objects. Pass property definition to reduce unnecessary validations for better performance.
            return deserialize(value, objDefinition, typeToUse, options, genericType);
        }
    }
    return value;
}
//# sourceMappingURL=deserialize.js.map